/**
 * persona-form-fields.tsx — Form tính cách bot (tên xưng, giọng, luôn làm,
 * cấm). Người dùng điền form, BE biên thành persona — không ai phải viết
 * prompt thô hay thấy `{{biến}}`.
 */
import { useEffect, useState } from 'react'
import { Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { previewPersona, type PersonaForm, type VaiTroBot } from '@/hooks/use-ai'
import { apiError } from '@/lib/api-client'

export const PERSONA_FORM_TRONG: PersonaForm = { tenXung: 'em', giongDieu: 'than_mat', luonLam: [], cam: [] }

/** Dòng ↔ mảng: người dùng gõ mỗi dòng một ý. */
const raDong = (xs: string[]) => xs.join('\n')
const raMang = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean).slice(0, 10)

export function PersonaFormFields({ value, onChange, role, disabled }: {
  value: PersonaForm
  onChange: (v: PersonaForm) => void
  role: VaiTroBot
  disabled?: boolean
}) {
  // Giữ text thô của hai ô nhiều dòng để gõ dở không bị chuẩn hoá mất dòng trống.
  const [luonLamText, setLuonLamText] = useState(raDong(value.luonLam))
  const [camText, setCamText] = useState(raDong(value.cam))
  useEffect(() => { setLuonLamText(raDong(value.luonLam)); setCamText(raDong(value.cam)) }, [value.luonLam, value.cam])

  const [xemTruoc, setXemTruoc] = useState<string | null>(null)
  const [dangXem, setDangXem] = useState(false)

  async function xem() {
    setDangXem(true)
    try {
      const r = await previewPersona({ ...value, luonLam: raMang(luonLamText), cam: raMang(camText) }, role)
      setXemTruoc(r.personaPrompt)
    } catch (err) {
      setXemTruoc(`Không xem trước được: ${apiError(err)}`)
    } finally {
      setDangXem(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Bot xưng là</Label>
          <Input value={value.tenXung} onChange={(e) => onChange({ ...value, tenXung: e.target.value })} placeholder="em" disabled={disabled} />
        </div>
        <div>
          <Label className="text-xs">Giọng điệu</Label>
          <div className="mt-1 flex gap-1 rounded-md border p-0.5">
            {([['than_mat', 'Thân mật'], ['trang_trong', 'Trang trọng']] as const).map(([k, nhan]) => (
              <button
                key={k} type="button" disabled={disabled}
                onClick={() => onChange({ ...value, giongDieu: k })}
                className={`flex-1 rounded px-2 py-1 text-sm ${value.giongDieu === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              >
                {nhan}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div>
        <Label className="text-xs">Luôn làm <span className="font-normal text-muted-foreground">(mỗi dòng một ý, tối đa 10)</span></Label>
        <textarea
          className="mt-1 min-h-[88px] w-full rounded-md border bg-transparent p-2 text-sm outline-none focus:ring-1 focus:ring-ring"
          value={luonLamText} disabled={disabled}
          onChange={(e) => setLuonLamText(e.target.value)}
          onBlur={() => onChange({ ...value, luonLam: raMang(luonLamText) })}
          placeholder={'Hỏi nhu cầu trước khi gợi ý sản phẩm\nBáo giá kèm quy cách'}
        />
      </div>
      <div>
        <Label className="text-xs">Không bao giờ <span className="font-normal text-muted-foreground">(mỗi dòng một ý)</span></Label>
        <textarea
          className="mt-1 min-h-[72px] w-full rounded-md border bg-transparent p-2 text-sm outline-none focus:ring-1 focus:ring-ring"
          value={camText} disabled={disabled}
          onChange={(e) => setCamText(e.target.value)}
          onBlur={() => onChange({ ...value, cam: raMang(camText) })}
          placeholder={'Không mời thêm sản phẩm khi khách đã chốt'}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => void xem()} disabled={dangXem}>
          <Eye className="h-3.5 w-3.5" /> {dangXem ? 'Đang biên…' : 'Xem prompt sẽ sinh ra'}
        </Button>
        <span className="text-[11px] text-muted-foreground">Luật đỏ (không cam kết chữa bệnh, không bịa giá…) tự thêm.</span>
      </div>
      {xemTruoc && (
        <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-2 text-[11px]">{xemTruoc}</pre>
      )}
    </div>
  )
}

/** Chuẩn hoá trước khi gửi BE (đề phòng ô textarea chưa blur). */
export function chotPersonaForm(v: PersonaForm): PersonaForm {
  return { ...v, tenXung: v.tenXung.trim() || 'em', luonLam: v.luonLam.slice(0, 10), cam: v.cam.slice(0, 10) }
}
